import type {
  DesktopBackupAgentStatus,
  DesktopBridge,
  DesktopClientStatus,
  DesktopHubStatus,
  DesktopOfflineState,
  DesktopSetupState,
  DesktopSyncSummary,
  DesktopUpdateState,
} from '@dob/domain';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

declare global {
  interface Window {
    /** Present only inside Dob Desktop (its preload script, D118). */
    readonly dob?: DesktopBridge;
  }
}

/** Dob Desktop's bridge, or `null` in a normal browser (the admin web). */
export const desktop: DesktopBridge | null =
  typeof window !== 'undefined' && window.dob?.kind === 'dob-desktop' ? window.dob : null;

/** Version and server of this PC's Dob Desktop (`undefined` in the admin web). */
export function useDesktopInfo() {
  return useQuery({
    queryKey: ['desktop', 'info'],
    queryFn: () => (desktop ? desktop.info() : null),
    enabled: desktop !== null,
    staleTime: Infinity,
  }).data;
}

/**
 * The PC's outbox as the sync indicator shows it. `undefined` = not asked yet or the admin web;
 * `null` = the PC is not registered yet, so nothing can wait to be sent.
 */
export function useDesktopSync(): DesktopSyncSummary | null | undefined {
  const [status, setStatus] = useState<DesktopSyncSummary | null | undefined>(undefined);
  useEffect(() => {
    if (!desktop) return;
    let active = true;
    void desktop.syncStatus().then((s) => {
      if (active) setStatus(s);
    });
    const stop = desktop.onSyncStatus((s) => {
      setStatus(s);
    });
    return () => {
      active = false;
      stop();
    };
  }, []);
  return status;
}

/**
 * This PC's registration (M7-2): `undefined` while asking, `null` in the admin web (no wizard).
 * Follows changes made in the background (e.g. the server revoked the PC while it was in use).
 */
export function useDesktopSetup(): DesktopSetupState | null | undefined {
  const [state, setState] = useState<DesktopSetupState | null | undefined>(
    desktop ? undefined : null,
  );
  useEffect(() => {
    if (!desktop) return;
    let active = true;
    void desktop.setup.state().then((s) => {
      if (active) setState(s);
    });
    const stop = desktop.setup.onState((s) => {
      setState(s);
    });
    return () => {
      active = false;
      stop();
    };
  }, []);
  return state;
}

/**
 * The hub status (M7-3): `undefined` while asking or in the admin web, `null` when this PC is not
 * its location's hub. Follows every change pushed by the main process.
 */
export function useDesktopHub(): DesktopHubStatus | null | undefined {
  const [status, setStatus] = useState<DesktopHubStatus | null | undefined>(undefined);
  useEffect(() => {
    if (!desktop) return;
    let active = true;
    void desktop.hub.status().then((s) => {
      if (active) setStatus(s);
    });
    const stop = desktop.hub.onStatus((s) => {
      setStatus(s);
    });
    return () => {
      active = false;
      stop();
    };
  }, []);
  return status;
}

/**
 * Client mode (M7-4): how this ordinary PC reaches its hub. `undefined` while asking or in the
 * admin web, `null` when this PC is not an ordinary registered PC (a hub, or not registered).
 */
export function useDesktopClient(): DesktopClientStatus | null | undefined {
  const [status, setStatus] = useState<DesktopClientStatus | null | undefined>(undefined);
  useEffect(() => {
    if (!desktop) return;
    let active = true;
    void desktop.client.status().then((s) => {
      if (active) setStatus(s);
    });
    const stop = desktop.client.onStatus((s) => {
      setStatus(s);
    });
    return () => {
      active = false;
      stop();
    };
  }, []);
  return status;
}

/**
 * Offline sign-in on this PC (M7-5): `undefined` while asking or in the admin web. Follows every
 * change pushed by the main process (signed in, signed out, ended because the person may no longer
 * work offline here).
 */
export function useDesktopOffline(): DesktopOfflineState | undefined {
  const [state, setState] = useState<DesktopOfflineState | undefined>(undefined);
  useEffect(() => {
    if (!desktop) return;
    let active = true;
    void desktop.offline.state().then((s) => {
      if (active) setState(s);
    });
    const stop = desktop.offline.onState((s) => {
      setState(s);
    });
    return () => {
      active = false;
      stop();
    };
  }, []);
  return state;
}

/**
 * Updates (M7-7): `undefined` while asking or in the admin web, `null` when this PC's Dob Desktop
 * has no updater. Follows every change pushed by the main process (progress, window, installing).
 */
export function useDesktopUpdate(): DesktopUpdateState | null | undefined {
  const [state, setState] = useState<DesktopUpdateState | null | undefined>(undefined);
  useEffect(() => {
    if (!desktop) return;
    let active = true;
    void desktop.update.state().then((s) => {
      if (active) setState(s);
    });
    const stop = desktop.update.onState((s) => {
      setState(s);
    });
    return () => {
      active = false;
      stop();
    };
  }, []);
  return state;
}

/**
 * The office Backup Agent (M7-8): its copy, folder and Windows task. `undefined` = not asked yet or
 * the admin web; `null` = this PC is not the Backup Agent.
 */
export function useDesktopBackupAgent(): DesktopBackupAgentStatus | null | undefined {
  const [status, setStatus] = useState<DesktopBackupAgentStatus | null | undefined>(undefined);
  useEffect(() => {
    if (!desktop) return;
    let active = true;
    void desktop.backupAgent.status().then((s) => {
      if (active) setStatus(s);
    });
    const stop = desktop.backupAgent.onStatus((s) => {
      setStatus(s);
    });
    return () => {
      active = false;
      stop();
    };
  }, []);
  return status;
}
